import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { SEOHead, JsonLd } from "@/components/SEO";
import { VendorCard, ReviewSnippet } from "@/pages/seo/_components";
import { Calendar, MapPin, Users, Layers, History } from "lucide-react";

/**
 * /health-fairs/{city-slug} — single-market detail page.
 *
 * Owns "health fair {city}" search intent. Content focus: events (upcoming
 * + past archive) plus the vendors / categories that have actually shown
 * up. JSON-LD: BreadcrumbList + one Event for each upcoming fair.
 */
export default function HealthFairMarketPage() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/seo/health-fairs/${slug}`);
                if (!cancelled) setData(r.data);
            } catch (e) { if (!cancelled) setErr(true); }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (err) {
        return (
            <Shell>
                <SEOHead title="Market not found" noindex />
                <div className="max-w-2xl mx-auto px-6 py-24 text-center">
                    <h1 className="font-heading text-3xl font-extrabold">We don’t track that market yet</h1>
                    <p className="text-brand-mute mt-3">No health fair history found for <span className="font-mono">/{slug}</span>.</p>
                    <Link to="/health-fairs" className="pill btn-forest px-5 py-3 font-semibold inline-block mt-6">All markets</Link>
                </div>
            </Shell>
        );
    }
    if (!data) return <Shell><div className="p-10 text-brand-mute">Loading…</div></Shell>;

    const loc = data.location;
    const cityLabel = `${loc.city}${loc.state ? `, ${loc.state}` : ""}`;
    const u = data.upcoming_count;
    const p = data.past_count;
    const titleCore = `Corporate health fairs in ${cityLabel}`;
    const description = `${u} upcoming and ${p} past employee health fair${u + p === 1 ? "" : "s"} in ${cityLabel}. Browse events, featured vendors, and host reviews on HealthFairX.`;

    const breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? window.location.origin : "" },
            { "@type": "ListItem", position: 2, name: "Health fairs", item: typeof window !== "undefined" ? `${window.location.origin}/health-fairs` : "" },
            { "@type": "ListItem", position: 3, name: cityLabel },
        ],
    };
    // Emit Event JSON-LD for every upcoming fair (Google Events surface).
    const eventLd = data.upcoming_events.map((ev) => ({
        "@context": "https://schema.org",
        "@type": "Event",
        name: ev.name,
        startDate: ev.event_date,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
            "@type": "Place",
            name: cityLabel,
            address: {
                "@type": "PostalAddress",
                addressLocality: loc.city,
                addressRegion: loc.state,
            },
        },
        url: typeof window !== "undefined" ? `${window.location.origin}/events/${ev.event_slug || ev.event_id}` : undefined,
        organizer: { "@type": "Organization", name: "HealthFairX" },
    }));

    return (
        <Shell>
            <SEOHead title={titleCore} description={description} />
            <JsonLd data={breadcrumb} />
            {eventLd.map((d, i) => <JsonLd key={i} data={d} />)}

            <section className="bg-brand-bone border-b border-brand-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
                    <div className="text-xs uppercase tracking-wider text-brand-mute font-bold flex items-center gap-2">
                        <Link to="/health-fairs" className="hover:text-brand-forest">Health fairs</Link>
                        <span>/</span>
                        <span className="text-brand-ink">{cityLabel}</span>
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-3 flex items-center gap-3" data-testid="hf-market-h1">
                        <MapPin size={36} className="text-brand-forest" /> Health fairs in {cityLabel}
                    </h1>
                    <p className="mt-3 text-brand-mute max-w-3xl">
                        {u > 0
                            ? `${u} upcoming and ${p} past employee health fair${u + p === 1 ? "" : "s"} listed in ${cityLabel}. Browse events, see who has attended, and read host reviews.`
                            : p > 0
                                ? `${p} past health fair${p === 1 ? "" : "s"} on record in ${cityLabel}. No upcoming events at the moment — `
                                : `No health fairs are listed in ${cityLabel} yet — `}
                        {u === 0 && (
                            <Link to="/register" className="text-brand-forest font-semibold underline">host one with HealthFairX</Link>
                        )}
                        {u === 0 && "."}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-3 text-sm">
                        <Link to={`/vendors/${loc.slug}`} data-testid="hf-link-vendors-in-city" className="pill px-4 py-2 font-semibold bg-white border border-brand-line hover:border-brand-forest hover:bg-brand-mint">
                            Vendors serving {cityLabel} →
                        </Link>
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-12">
                {/* Upcoming fairs */}
                {data.upcoming_events.length > 0 && (
                    <section data-testid="hf-upcoming">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <Calendar size={20} className="text-brand-forest" /> Upcoming health fairs
                        </h2>
                        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.upcoming_events.map((ev) => (
                                <Link key={ev.event_id} to={`/events/${ev.event_slug || ev.event_id}`} data-testid={`hf-upcoming-${ev.event_id}`}
                                      className="rounded-xl border border-brand-line bg-white p-5 card-hover block">
                                    <div className="text-xs text-brand-mute">{ev.event_date}{ev.start_time ? ` · ${ev.start_time}` : ""}</div>
                                    <div className="font-heading font-bold mt-1">{ev.name}</div>
                                    <div className="text-xs text-brand-mute mt-1">{cityLabel}</div>
                                    {ev.total_employees ? (
                                        <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-forest">
                                            <Users size={12} /> {ev.total_employees.toLocaleString()} employees
                                        </div>
                                    ) : null}
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Featured vendors */}
                {data.featured_vendors.length > 0 && (
                    <section data-testid="hf-featured-vendors">
                        <h2 className="font-heading text-2xl font-bold">Vendors with {cityLabel} experience</h2>
                        <p className="text-sm text-brand-mute mt-1">Ranked by past fairs attended in this market.</p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
                            {data.featured_vendors.map((v) => (
                                <div key={v.id} className="relative">
                                    <VendorCard v={v} testid={`hf-vendor-${v.id}`} />
                                    {v.fairs_in_market > 0 && (
                                        <span className="absolute top-3 left-3 bg-brand-forest text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                                            {v.fairs_in_market} fair{v.fairs_in_market === 1 ? "" : "s"} here
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Categories represented — internal-link tower to /vendors/{city}/{cat} */}
                {data.categories_present.length > 0 && (
                    <section data-testid="hf-categories">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <Layers size={20} className="text-brand-forest" /> Vendor categories at {cityLabel} health fairs
                        </h2>
                        <div className="flex flex-wrap gap-2 mt-4">
                            {data.categories_present.map((c) => (
                                <Link key={c.id} to={`/vendors/${loc.slug}/${c.slug}`}
                                      data-testid={`hf-cat-${c.slug}`}
                                      className="pill px-4 py-2 text-sm font-semibold bg-white border border-brand-line hover:border-brand-forest hover:bg-brand-mint">
                                    {c.name} <span className="text-xs text-brand-mute ml-1.5">({c.vendor_count})</span>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Past fairs archive */}
                {data.past_events.length > 0 && (
                    <section data-testid="hf-past">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <History size={20} className="text-brand-mute" /> Past health fairs in {cityLabel}
                        </h2>
                        <ul className="mt-5 grid sm:grid-cols-2 gap-3">
                            {data.past_events.map((ev) => (
                                <li key={ev.event_id} className="rounded-xl border border-brand-line bg-white p-4" data-testid={`hf-past-${ev.event_id}`}>
                                    <div className="text-xs text-brand-mute">{ev.event_date}</div>
                                    <Link to={`/events/${ev.event_slug || ev.event_id}`} className="font-semibold hover:underline block mt-1">{ev.name}</Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Recent reviews */}
                {data.recent_reviews.length > 0 && (
                    <section data-testid="hf-reviews">
                        <h2 className="font-heading text-2xl font-bold">Recent reviews from {cityLabel} hosts</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
                            {data.recent_reviews.map((r) => <ReviewSnippet key={r.id} r={r} />)}
                        </div>
                    </section>
                )}
            </div>
        </Shell>
    );
}
